<script setup>
import { gsap, ScrollTrigger } from '~/vendors/gsap'
import SplitType from 'split-type'
import { onLoadAnimationEnd } from '~~/composables/onLoadAnimationEnd'
import {
  eyebrowAnimation,
  headlineAnimation,
  dividerAnimationLeft,
  mediaCardSlideAnimation,
} from '~/utils/animations'

import GlobalEmitter from '~~/glxp/utils/emitter'

const heroRef = ref(null)
const progress = ref(0)
let stProgress
let stScene

const props = defineProps({
  eyebrow: {
    type: String,
    required: true,
  },
  headline: {
    type: String,
    required: true,
  },
})

/**
 * Timeline
 */
const isActive = computed(() => progress.value <= 0.45)
const isVisible = ref(true)
let hasInitialized = false
let tl0
let typeInstance0
let typeInstance1

function doSplits() {
  typeInstance0 = new SplitType(
    heroRef.value.querySelector('.section-header__eyebrow'),
    { types: 'chars' }
  )
  typeInstance1 = new SplitType(
    heroRef.value.querySelector('.section-header__headline'),
    { types: 'lines' }
  )
}

function initTimelines() {
  const { chars: eyebrowChars } = typeInstance0
  const { lines: headlineLines } = typeInstance1

  // if re-initializing, fast forward to end to clear props, then kill
  tl0?.seek(tl0.duration())
  tl0?.kill()
  tl0 = gsap
    .timeline({ paused: true, clearProps: true })
    .addLabel('eyebrow')
    .from(eyebrowChars, eyebrowAnimation, 'eyebrow')
    .from(
      heroRef.value.querySelector('.horizontal-rule'),
      dividerAnimationLeft,
      'eyebrow+=0.25'
    )

    // headline
    .addLabel('headline', 'eyebrow+=0.25')
    .from(headlineLines, headlineAnimation, 'headline')

    // notification card
    .from(
      heroRef.value.querySelector('.home-hero__notifications'),
      mediaCardSlideAnimation,
      'headline+=0.5'
    )

    // scroll down button
    .from(
      heroRef.value.querySelector('.scroll-down-button'),
      {
        opacity: 0,
        duration: 0.5,
      },
      'headline+=0.5'
    )

  if (isActive.value && hasInitialized) {
    tl0.seek(tl0.duration())
  }

  hasInitialized = true
}

watch(isActive, (value) => {
  if (value) {
    tl0.timeScale(1).play()
  } else {
    tl0.timeScale(3).reverse()
  }
})

function playIntro() {
  tl0.play()
}

let heightTween
function setHeight() {
  const scrollEl = heroRef.value.querySelector('.home-hero__scroller')
  heightTween?.kill()
  heightTween = gsap.to(scrollEl, {
    minHeight: window.innerHeight,
  })
}

function init() {
  doSplits()
  initTimelines()
  setHeight()
}

onMounted(() => {
  init()
  setHeight()
  GlobalEmitter.on('debouncedResize', init)
})

onLoadAnimationEnd(async () => {
  await nextTick()
  playIntro()
})

onBeforeUnmount(() => {
  tl0?.kill()
  GlobalEmitter.off('debouncedResize', init)
})

/**
 * ScrollTriggers
 */
onMounted(() => {
  stProgress = ScrollTrigger.create({
    trigger: heroRef.value,
    start: 'top top',
    end: 'bottom 60%',
    // markers: true,
    onUpdate: ({ progress: tlProgress }) => {
      progress.value = tlProgress
      GlobalEmitter.emit('webgl_home_header_progress', { progress: tlProgress })
    },
  })

  stScene = ScrollTrigger.create({
    trigger: heroRef.value,
    start: 'top top',
    end: 'bottom -200px',
    onEnterBack: () => {
      GlobalEmitter.emit('webgl_home_switch_subscene', { isInstant: true })
      GlobalEmitter.emit('webgl_raf_start')
      isVisible.value = true
    },
    onLeave: () => {
      GlobalEmitter.emit('webgl_home_switch_subscene', { isInstant: true })
      isVisible.value = false
      requestAnimationFrame(() => {
        GlobalEmitter.emit('webgl_raf_stop')
      })
    },
  })
})

onBeforeUnmount(() => {
  stProgress?.kill()
  stScene?.kill()
})
</script>

<template>
  <section
    ref="heroRef"
    class="home-hero"
    :class="{ 'home-hero--hidden': !isVisible }"
  >
    <div class="home-hero__scroller">
      <div class="home-hero__wrapper container">
        <div class="home-hero__header">
          <SectionHeader
            :eyebrow="eyebrow"
            :title="headline"
            alignment="left"
            level="h1"
          />
        </div>

        <div class="home-hero__notifications">
          <GlobalNotifications class="gtm-vgms-global-live-unity-25" />
        </div>

        <ScrollDownButton />
      </div>
    </div>
  </section>
</template>

<style lang="scss">
// Vendors
@import 'styles/vendors/sass-mq/mq';

// Utils
@import 'styles/utils/functions';

.home-hero {
  height: 200vh;
  @include mq($from: 'l') using ($from) {
    height: 400vh;
  }
  position: relative;

  &--hidden {
    visibility: hidden;
  }

  &__scroller {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    min-height: var(--real-height, 100vh);
  }

  &__wrapper {
    position: relative;
    padding-bottom: 30px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;

    @include mq($from: 'l') using ($from) {
      padding-bottom: 56px;
    }
  }

  &__header {
    // padding-top: 275px;
    width: gs(5, 5, 'xxxs');

    @include mq($from: 'l') using ($from) {
      // padding-top: 0px;
      margin-left: gs(2, 2, 'l');
      width: gs(9, 8, $from);
    }

    @include mq($until: 'l') using ($until) {
      margin-top: auto;
    }

    @include mq($until: 'l') using ($until) {
      .section-header__eyebrow {
        margin-bottom: 14px;
      }

      .section-header__divider + .section-header__headline {
        margin-top: 32px;
      }
    }
  }

  &__headline {
    margin-left: -0.08em;
    max-width: 500px;
    display: block;
  }

  &__notifications {
    margin-top: 48px;
    @include mq($from: 'l') using ($from) {
      padding: inherit;
      position: absolute;
      left: 0;
      bottom: 0;
      margin-top: 0;
    }
  }

  .scroll-down-button {
    position: absolute;
    bottom: 56px;
    right: 56px;

    @include mq($until: 'm') using ($until) {
      display: none;
    }
  }
}
</style>
